import React from "react"
import _ from "lodash"
import SampleWaveform from "./SampleWaveform"
import { styled } from "@mui/material/styles"
import { css } from '@emotion/react';

import { observer, inject } from "mobx-react"
import { logEvent } from "../utils/analytics"

const SampleWaveformContainer = styled('div')`
  height: 150px;
  position: relative;
  display: block;
`

const AuthorNameRow = styled('div')`
  color: #7f7f7f;
  font-size: 12px;
  padding-top: 7px;
  display: flex;
  align-items: center;

  @media all and (max-width: 600px) {
    font-size: 14px;
  }

  .by-author {
    padding-right: 4px;
  }

  .sample-card-avatar {
    height: 20px;
    padding: 0 7px;
  }
`

const WidgetIcon = styled('i')`
  margin-right: 0;
  font-size: 11px;
`

const CardActionButton = styled('span')`
  color: ${(props) => (props.isPlaying ? '#ee534f' : '#4db6ac')};
  border-right: 1px solid #ECECEC;
  border-bottom-left-radius: 5px;

  &:hover {
    color: ${(props) => (props.isPlaying ? '#f38784' : '#80cbc4')};
  }
`

const FavoriteIconAndCount = styled('a')`
  display: flex;
  transition: ${(props) => (props.isFavorited ? "color .3s ease" : null)};
  color: ${(props) => (props.isFavorited ? "#E2264D" : null)};

  i {
    margin-right: 3px;
  }
`

@inject((props, stores, context) => props)
@observer
export default class SampleCard extends React.Component {
  static defaultProps = {
    showSampleSelect: false,
  }

  constructor(props) {
    super(props)
    this.sampleWaveform = React.createRef()
  }

  componentDidMount() {
    this.checkIfFavorite()
    this.checkIfDownloaded()
    if (typeof window !== "undefined") {
      window.addEventListener("favoritesReceivedEvent", this.checkIfFavorite)
      window.addEventListener("downloadsReceivedEvent", this.checkIfDownloaded)
    }
  }

  componentWillUnmount() {
    if (typeof window !== "undefined") {
      window.removeEventListener("favoritesReceivedEvent", this.checkIfFavorite)
      window.removeEventListener("downloadsReceivedEvent", this.checkIfDownloaded)
    }
  }

  checkIfFavorite = () => {
    if (typeof localStorage !== "undefined") {
      const { sample } = this.props
      const { id } = sample
      const favoriteSamples = localStorage.getItem("favoriteSamples")
        ? localStorage.getItem("favoriteSamples")
        : []
      const isFavorited = favoriteSamples.includes(id)
      if (isFavorited) {
        sample.setToFavorite()
      }
    }
  }

  checkIfDownloaded = () => {
    if (typeof localStorage !== "undefined") {
      const { sample } = this.props
      const { id } = sample
      const downloadedSamples = localStorage.getItem("downloadedSamples")
        ? localStorage.getItem("downloadedSamples")
        : []
      const isDownloaded = downloadedSamples.includes(id)
      if (isDownloaded) {
        sample.setToDownloaded()
      }
    }
  }

  handlePlaySoundOnce = () => {
    this.sampleWaveform.current.playSoundOnce()
  }

  handleStopSound = () => {
    this.sampleWaveform.current.stopSound()
  }

  handleActionBtnClick = () => {
    const { sample } = this.props

    if (!sample.isPlaying) {
      this.handlePlaySoundOnce()
    } else {
      this.handleStopSound()
    }
  }

  handleFavoriteClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const { sample } = this.props
    const { isFavorited } = sample

    if (Helpers.isLoggedIn() == false) {
      FlashMessages.notifyUserIsLoggedOut()
      return
    }

    if (Boolean(isFavorited)) {
      sample.unfavorite()
    } else {
      sample.favorite()
    }
  }

  toggleSampleSelection = () => {
    const { sample, sampleStore } = this.props
    if (!sampleStore) {
      console.error("sampleStore not availabe")
    }
    if (sample.isSelected) {
      sampleStore.deselectSample(sample)
    } else {
      sampleStore.selectSample(sample)
    }
  }

  handleWaveformClick = (e) => {
    const { sample, showSampleSelect } = this.props
    if (e.shiftKey || e.metaKey) {
      window.open(sample.sample_url, "_blank").focus()
    } else {
      if (showSampleSelect) {
        this.toggleSampleSelection()
      } else {
        window.location.href = sample.sample_url
      }
    }
  }

  handleDownloadButtonClick = () => {
    const { sample } = this.props
    sample.setToDownloaded()
    logEvent("Download sample click")
  }

  renderCardActionButton() {
    const { sample } = this.props

    const iconClassname = sample.isPlaying
      ? "fas fa-stop"
      : "fas fa-play"

    const handleKeyDown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        this.handleActionBtnClick()
      }
    }

    return (
      <CardActionButton
        className="card-action-button waves-effect waves-light"
        onClick={this.handleActionBtnClick}
        onKeyDown={handleKeyDown}
        isPlaying={sample.isPlaying}
        tabIndex="0"
        aria-label={`Play/stop`}
        role="button"
      >
        <i className={iconClassname} aria-hidden="true"></i>
      </CardActionButton>
    );
  }

  renderFavoriteIconAndCount() {
    const { sample } = this.props
    const { isFavorited, favorites_count } = sample
    const iconClassname = Boolean(isFavorited) ? "fas fa-heart" : "far fa-heart"

    return (
      <FavoriteIconAndCount
        href="#"
        onClick={this.handleFavoriteClick}
        isFavorited={Boolean(isFavorited)}
        aria-label={`Favorite/unfavorite button and total favorites count `}
        role="button"
      >
        <i className={iconClassname} aria-hidden="true"></i>
        <span>{favorites_count > 0 && favorites_count}</span>
      </FavoriteIconAndCount>
    )
  }

  render() {
    const { sample, showSampleSelect } = this.props
    const {
      tempo,
      mode,
      key,
      user_avatar_url,
      user_slug,
      published_at,
      user_display_name_truncated,
      sample_tags,
      slug,
      duration,
    } = sample

    const user_path = "/users/" + user_slug
    const sample_download_path = "/samples/" + slug + "/download"
    const approximate_duration = Number.parseFloat(duration).toFixed(1)
    const sampleTagsToShow = _.sortBy(sample_tags, ['name']).slice(0,7);

    return (
      <div className="sample-card col m3 s12 l3">
        <div className="card hoverable">
          <SampleWaveformContainer>
            <SampleWaveform
              sample={sample}
              ref={this.sampleWaveform}
              shouldLink={true}
              handleWaveformClick={this.handleWaveformClick}
              showSampleSelect={showSampleSelect}
            />
          </SampleWaveformContainer>
          <div className="card-content">
            <ul className="sample-tags">
              {sampleTagsToShow &&
                sampleTagsToShow.map((tag) => {
                  const { slug, name } = tag
                  const tagPath = "/tag/" + slug
                  return (
                    <li className="tag-list-item" key={slug}>
                      <a className="tag-link" href={tagPath} aria-label={`Link to tag: '${name}'`}>
                        {_.capitalize(name)}
                      </a>
                    </li>
                  )
                })}
            </ul>
            <ul className="sample-attrs">
              {tempo && (
                <li aria-label={`Tempo`}>
                  <WidgetIcon className="fas fa-ellipsis" aria-hidden="true"></WidgetIcon>{" "}
                  {tempo}bpm
                </li>
              )}
              {key && (
                <li aria-label={`Key`}>
                  <WidgetIcon className="fas fa-music" aria-hidden="true"></WidgetIcon> {key}{" "}
                  {mode}
                </li>
              )}
              {approximate_duration && (
                <li aria-label={`Duration`}>
                  <WidgetIcon className="far fa-clock" aria-hidden="true"></WidgetIcon>{" "}
                  {approximate_duration}s
                </li>
              )}
            </ul>
            <div className="card-links">
              <AuthorNameRow>
                <div className="favorite-attr">
                  {this.renderFavoriteIconAndCount()}
                </div>

                <span className="by-author">By</span>
                <a href={user_path} aria-label={`Link to author`}>{user_display_name_truncated}</a>
                {user_avatar_url && (
                  <a
                    css={css`
                      display: flex;
                    `}
                    href={user_path}
                    role="presentation"
                    tabIndex="-1"
                  >
                    <img
                      src={user_avatar_url}
                      className="circle sample-card-avatar"
                      alt={`Author avatar`}
                      aria-hidden="true"
                    />
                  </a>
                )}
                {!published_at && (
                  <span className="red-text" aria-label="This sample is unpublished" role="status">
                    Unpublished
                  </span>
                )}
              </AuthorNameRow>
            </div>
          </div>
          <div className="card-action">
            {this.renderCardActionButton()}
            <a
              href={sample_download_path}
              data-method="post"
              data-remote="true"
              rel="nofollow"
              className="download-sample-link card-action-button waves-light waves-effect"
              onClick={this.handleDownloadButtonClick}
              aria-label={`Download`}
              role="button"
            >
              {sample.isDownloaded ? (
                <i className="fa-solid fa-arrows-rotate"></i>
              ) : (
                <i className="fas fa-download"></i>
              )}
            </a>
          </div>
        </div>
      </div>
    )
  }
}